import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import axios from 'axios';

import { apiSession } from '~/@core/infrastructure/api/config/gfpApi';
import { buildUrl } from '~/helpers/util';
import { singleFlightRefresh } from '~/services/auth/refresh.lock';
import { emitTokenChanged } from '~/services/auth/token.events';
import { logoutSession } from '~/services/sessions/SessionsService';
import { clearExpiryCache } from '../token/useTokenExpiryStore';
import { useTokenStore } from '../token/useTokenStore';

export type IRefreshData = {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  scope?: string;
};

export type IHandleAxiosErrorOptions = {
  tokenEndpoint?: string;
};

export type IRetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

export type IUseAuthStore = {
  refreshToken: () => Promise<string>;
  forceRefresh: () => Promise<string>;
  getTokenToHeader: () => string;
  logoutLocal: () => void;
  logout: () => Promise<void>;
  redirectSingleSignOn: () => void;
  handleAxiosError: (
    error: unknown,
    client?: AxiosInstance,
    options?: IHandleAxiosErrorOptions,
  ) => Promise<AxiosResponse<unknown>>;
};

const DEFAULT_TOKEN_ENDPOINT = '/connect/token';

// ==================== helpers ====================

const redirectToSSOLogoff = (): boolean => {
  const ssoBase = process.env.REACT_APP_SINGLE_SIGNON?.trim() ?? '';

  if (!ssoBase) {
    console.error('REACT_APP_SINGLE_SIGNON não definida!');
    return false;
  }

  const url = buildUrl(ssoBase, 'logoff');
  window.location.href = url;
  return true;
};

const resolveTokenEndpoint = (value?: string): string => {
  return value ?? DEFAULT_TOKEN_ENDPOINT;
};


const getConfig = (error: AxiosError<unknown>): IRetryableRequestConfig | undefined => {
  return error.config as IRetryableRequestConfig | undefined;
};

const getUrl = (config?: AxiosRequestConfig): string => {
  return String(config?.url ?? '');
};

const getStatus = (error: AxiosError<unknown>): number => {
  return error.response?.status ?? 0;
};

const isTokenEndpointCall = (url: string, tokenEndpoint: string): boolean => {
  return url.includes(tokenEndpoint);
};

const hasRefreshToken = (): boolean => {
  return Boolean(useTokenStore.getRefresh());
};

let isLoggingOut = false;

const setAuthHeader = (
  config: IRetryableRequestConfig,
  accessToken: string,
): void => {
  const tokenType = useTokenStore.getTokenType() || 'Bearer';

  config.headers = config.headers ?? {};
  config.headers.Authorization = `${tokenType} ${accessToken}`;
};

// ==================== service ====================

export const useAuthStore: IUseAuthStore = {
  refreshToken: async (): Promise<string> => {
    const refreshTokenValue = useTokenStore.getRefresh();
    const scope = useTokenStore.getScope();

    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: refreshTokenValue,
      client_id: process.env.REACT_APP_SSO_CLIENT_ID ?? '',
      client_secret: process.env.REACT_APP_SSO_CLIENT_SECRET ?? '',
      scope,
    });

    const { data } = await apiSession.post<IRefreshData>(
      DEFAULT_TOKEN_ENDPOINT,
      body.toString(),
      {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      },
    );

    useTokenStore.setAll({
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      expiresIn: String(data.expires_in),
      scope: data.scope ?? scope,
      tokenType: data.token_type,
    });

    clearExpiryCache();
    emitTokenChanged();

    return data.access_token;
  },

  forceRefresh: async (): Promise<string> => {
    if (!hasRefreshToken()) {
      await useAuthStore.logout();
      throw new Error('Missing refresh token');
    }

    return singleFlightRefresh(() => useAuthStore.refreshToken());
  },

  getTokenToHeader: (): string => {
    const access = useTokenStore.getAccess();
    const tokenType = useTokenStore.getTokenType() || 'Bearer';

    return access ? `${tokenType} ${access}` : '';
  },

  logoutLocal: (): void => {
    useTokenStore.clearAll();
    clearExpiryCache();
    emitTokenChanged();
  },

  logout: async (): Promise<void> => {
    if (isLoggingOut) return;
    isLoggingOut = true;

    const refresh = useTokenStore.getRefresh();
    const authorization = useTokenStore.getAuthorizationHeader();

    useAuthStore.logoutLocal();

    if (refresh) {
      try {
        await logoutSession(refresh, authorization);
      } catch {
        // best-effort: continua mesmo se backend falhar
      }
    }

    if (!redirectToSSOLogoff()) isLoggingOut = false;
  },

  redirectSingleSignOn: (): void => {
    redirectToSSOLogoff();
  },

  handleAxiosError: async (
    error: unknown,
    client: AxiosInstance = axios,
    options: IHandleAxiosErrorOptions = {},
  ): Promise<AxiosResponse<unknown>> => {
    const tokenEndpoint = resolveTokenEndpoint(options.tokenEndpoint);

    if (!axios.isAxiosError(error)) throw error;

    const axiosError = error as AxiosError<unknown>;
    const originalConfig = getConfig(axiosError);

    if (!originalConfig) throw axiosError;

    const url = getUrl(originalConfig);
    const status = getStatus(axiosError);

    if (isTokenEndpointCall(url, tokenEndpoint)) throw axiosError;

    if (status !== 401) throw axiosError;

    if (originalConfig._retry) throw axiosError;

    originalConfig._retry = true;

    try {
      const newAccess = await useAuthStore.forceRefresh();
      setAuthHeader(originalConfig, newAccess);

      return await client.request(originalConfig);
    } catch (refreshError) {
      await useAuthStore.logout();
      throw refreshError;
    }
  },
};